import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { deriveAppTypesIdsFromAppTypesNumber, deriveAppTypesNumberFromAppTypesIds } from '@/util/appTypes'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Textarea } from '@/components/shadcn/textarea'

import { AppTypesArray } from '@/util/appTypes'
import { FormPictogramPreview } from '@/components/form-components/FormPictogramPreview'
import { MultiSelectCombobox, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { StringListField } from '@/components/form-components/StringListField'
import { DynamicPictogram } from '@/components/pictograms/DynamicPictogram'
import { DynamicPictogramName } from '@/components/pictograms/types'
import { pictogramOptions } from '@/components/pictograms/utils/PictogramOptions'
import { normalizeFileExtension, parseFileTypes, serializeFileTypes } from '@/util/fileTypes'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsNewApplication = RestSchemas['Messages.Applications.NewApplication']
type MessagesApplicationsUpdateApplication = RestSchemas['Messages.Applications.UpdateApplication']

type AppTypes = RestSchemas['Common.AppTypes']

// Form validation schema
const formSchema = z.object({
  appName: z.string().min(1, 'Name is required').max(256, 'Name cannot exceed 256 characters'),
  appKey: z.string().min(1, 'Key is required').max(256, 'Key cannot exceed 256 characters'),
  description: z.string().max(256, 'Description cannot exceed 256 characters').optional(),
  status: z.string().min(1, 'Status is required'),
  maxFileSizeMb: z.number().min(1, 'Max file size must be at least 1 MB'),
  fileTypeExtensions: z
    .array(z.string().regex(/^\.[a-z0-9]+$/, 'Invalid file extension'))
    .superRefine((extensions, ctx) => {
      const serialized = serializeFileTypes(extensions)
      if (serialized.length > 256) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'File types cannot exceed 256 characters',
        })
      }
    }),
  appTypesIdsArray: z.array(z.number()),
  pictureUrl: z.string().optional().nullable(),
})

export type TApplicationForm = z.infer<typeof formSchema>

type ApplicationFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedApp?: MessagesApplicationsApplication
}

export const ApplicationForm = (props: ApplicationFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedApp
  const { postApplicationsApiUpdateApplication, putApplicationsApiAddApplication, getApplicationsApiGetApplications } =
    useRestClient()

  const { mutate: updateApplication, isPending: isUpdating } = postApplicationsApiUpdateApplication.useMutation(
    undefined,
    {
      onSuccess: () => {
        getApplicationsApiGetApplications.invalidateQueries({})
        if (props.sheetTrigger) {
          setSheetOpen(false)
        }
        toast.success('Application successfully updated')
        props.onSubmit?.()
      },
      onError: () => {
        toast.error('Error in updating application')
      },
    }
  )

  const { mutate: addApplication, isPending: isAdding } = putApplicationsApiAddApplication.useMutation(undefined, {
    onSuccess: () => {
      getApplicationsApiGetApplications.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Application successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error in adding application')
    },
  })

  const submitHandler = useCallback(
    (data: TApplicationForm) => {
      if (edit && props.selectedApp) {
        const updatedApp: MessagesApplicationsUpdateApplication = {
          id: props.selectedApp.applicationId!,
          name: data.appName,
          fileType: serializeFileTypes(data.fileTypeExtensions),
          appKey: data.appKey,
          description: data.description || '',
          status: data.status,
          maxFileSizeMb: data.maxFileSizeMb,
          appTypes: deriveAppTypesNumberFromAppTypesIds(data.appTypesIdsArray),
          pictureUrl: data.pictureUrl || null,
        }
        updateApplication({ body: updatedApp })
      } else {
        const newApp: MessagesApplicationsNewApplication = {
          name: data.appName,
          fileType: serializeFileTypes(data.fileTypeExtensions),
          appKey: data.appKey,
          description: data.description || '',
          status: data.status,
          maxFileSizeMb: data.maxFileSizeMb,
          appTypes: deriveAppTypesNumberFromAppTypesIds(data.appTypesIdsArray) as AppTypes,
          pictureUrl: data.pictureUrl || null,
        }
        addApplication({ body: newApp })
      }
    },
    [edit, props.selectedApp, addApplication, updateApplication]
  )

  const isLoading = isUpdating || isAdding

  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (props: ApplicationFormProps & { submitHandler: (data: TApplicationForm) => void }) => {
  const selectedApp: Partial<MessagesApplicationsApplication> = props.selectedApp ?? {}
  const { appName, appKey, description, status, maxFileSizeMb, fileTypes, appTypes, pictureUrl } = selectedApp

  const defaultValues = useMemo(() => {
    return {
      appName: appName ?? '',
      appKey: appKey ?? '',
      description: description ?? '',
      status: status ?? 'Developing',
      maxFileSizeMb: maxFileSizeMb ?? 50,
      fileTypeExtensions: parseFileTypes(fileTypes ?? '.csv/.xlsx'),
      appTypesIdsArray: appTypes ? deriveAppTypesIdsFromAppTypesNumber(appTypes) : [],
      pictureUrl: pictureUrl ?? '',
    }
  }, [appName, appKey, description, status, maxFileSizeMb, fileTypes, appTypes, pictureUrl])

  const form = useForm<TApplicationForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const watchPictureUrl = useWatch({ control: form.control, name: 'pictureUrl' })

  const pictogramSelectOptions = useMemo(
    () => pictogramOptions.map((name) => ({ value: name, label: name })),
    []
  )

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="appKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="appName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <SingleSelectComboboxField
                control={form.control}
                name="pictureUrl"
                label="Pictogram"
                options={pictogramSelectOptions}
                placeholder="Select a pictogram"
                searchPlaceholder="Search pictograms..."
                emptyMessage="No pictograms found."
                allowClear={true}
                renderSelectedOption={(option) => (
                  <PictogramOptionContent pictogramName={option.value} label={option.label} />
                )}
                renderOption={(option) => <PictogramOptionContent pictogramName={option.value} label={option.label} />}
              />
              <FormPictogramPreview pictogramName={watchPictureUrl} />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Developing">Developing</SelectItem>
                        <SelectItem value="Coming Soon">Coming Soon</SelectItem>
                        <SelectItem value="Live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxFileSizeMb"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max File Size (MB)</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <StringListField
                control={form.control}
                name="fileTypeExtensions"
                label="File Types"
                description="Type a file extension and press Enter to add it. Repeat for each type you want to allow — the dot is optional (e.g. xlsx or .xlsx)."
                placeholder="e.g. xlsx"
                normalizeEntry={(raw) => normalizeFileExtension(raw) || null}
              />

              <FormField
                control={form.control}
                name="appTypesIdsArray"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>App Types</FormLabel>
                    <FormControl>
                      <MultiSelectCombobox
                        options={AppTypesArray.filter((appType) => appType.roleId !== 0).map(
                          (appType): SelectOption<number> => ({
                            value: appType.roleId,
                            label: appType.roleName,
                            description: appType.roleDescription,
                          })
                        )}
                        selectedValues={field.value}
                        onSelectionChange={field.onChange}
                        placeholder="Select app types..."
                        searchPlaceholder="Search app types..."
                        emptyMessage="No app types found."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">Submit</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}

function PictogramOptionContent({ pictogramName, label }: { pictogramName: string; label: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <DynamicPictogram
        pictogramName={pictogramName as DynamicPictogramName}
        size={24}
        className="shrink-0"
        wrapperClassName="flex size-6 shrink-0 items-center justify-center"
        gradient
        lazyLoad={false}
        loadingFallback={<span className="size-6 shrink-0" />}
      />
      <span className="min-w-0 truncate font-medium">{label}</span>
    </div>
  )
}
